import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import QRTypeSelector from "@/pages/generator/components/QRTypeSelector";
import QRContentForm from "@/pages/generator/components/QRContentForm";
import QRDesignPanel from "@/pages/generator/components/QRDesignPanel";
import QRPreviewPanel from "@/pages/generator/components/QRPreviewPanel";

export type QRType = "url" | "vcard" | "wifi" | "pdf" | "instagram" | "whatsapp" | "facebook" | "text" | "email" | "phone";

export type QRTemplate = "classic" | "modern" | "business" | "social" | "gradient" | "dark" | "rounded" | "minimal" | "neon" | "card";

export interface QRConfig {
  type: QRType;
  content: string;
  fgColor: string;
  bgColor: string;
  size: number;
  errorLevel: "L" | "M" | "Q" | "H";
  template: QRTemplate;
  qrName: string;
  qrSubtext: string;
  brandText: string;
}

const defaultConfig: QRConfig = {
  type: "url",
  content: "https://example.com",
  fgColor: "#1e3a5f",
  bgColor: "#ffffff",
  size: 300,
  errorLevel: "M",
  template: "classic",
  qrName: "",
  qrSubtext: "",
  brandText: "",
};

export default function GeneratorPage() {
  const [config, setConfig] = useState<QRConfig>(defaultConfig);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const updateConfig = (partial: Partial<QRConfig>) => {
    setConfig((prev) => ({ ...prev, ...partial }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-16">
        {/* Step Header */}
        <div className="bg-white border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center gap-2 md:gap-6 overflow-x-auto">
              {[
                { num: 1, label: "QR Türünü Seçin" },
                { num: 2, label: "İçerik Ekleyin" },
                { num: 3, label: "Tasarım & İndir" },
              ].map((s, idx) => (
                <div key={s.num} className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                  <button
                    onClick={() => setStep(s.num as 1 | 2 | 3)}
                    className={`flex items-center gap-2 cursor-pointer transition-all ${
                      step === s.num ? "opacity-100" : "opacity-50 hover:opacity-75"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                        step >= s.num ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {step > s.num ? <i className="ri-check-line text-sm"></i> : s.num}
                    </div>
                    <span className={`text-sm font-medium whitespace-nowrap hidden sm:block ${step === s.num ? "text-slate-900" : "text-slate-400"}`}>
                      {s.label}
                    </span>
                  </button>
                  {idx < 2 && (
                    <div className={`w-8 md:w-16 h-0.5 ${step > s.num ? "bg-blue-400" : "bg-slate-200"}`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Panel */}
            <div className="flex-1">
              {step === 1 && (
                <QRTypeSelector
                  selected={config.type}
                  onSelect={(type) => { updateConfig({ type }); setStep(2); }}
                />
              )}
              {step === 2 && (
                <QRContentForm
                  config={config}
                  onChange={updateConfig}
                  onNext={() => setStep(3)}
                  onBack={() => setStep(1)}
                />
              )}
              {step === 3 && (
                <QRDesignPanel
                  config={config}
                  onChange={updateConfig}
                  onBack={() => setStep(2)}
                />
              )}
            </div>

            {/* Right Preview */}
            <div className="lg:w-80 xl:w-96">
              <QRPreviewPanel config={config} />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
